import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Mutation } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import { CREATE_FIXTURE_MUTATION } from '../../api/queries/fixtures/FixtureQueries';
import { Dropdown } from 'semantic-ui-react';
import GlobalShortcut from '../GlobalShortcut';
import ToolbarButton from '../toolbar/ToolbarButton';
import { isWithinMenu } from '../common/Menu';
import {
    getDocumentOptions,
    resolveDocumentSelection,
} from '../common/documentOptions';
import MixpanelLogger from '../../diagnostics/logging/mixpanelLogger';
import { KEY_CODES } from 'constant';
import store from 'store';
import { getDefaultTypeForGroup } from '../../models/Datasets';
import { getVesselCategory } from '_legacy/utils/vesselUtils';
import { getVesselPostProcessingService } from '_legacy/modules/columns/vessel/services/VesselPostProcessingServiceFactory';
import { VesselsApi } from '_legacy/api/vesselsApi';
import { isGroupOnly } from '_legacy/utils/Visibility';
import { typesByDataset } from '_legacy/models/Type';
import { parseTankersGroupType } from '_legacy/models/TankersGroupTypes';

const { KEY_F } = KEY_CODES;

export class AddFixture extends Component {
    constructor(props) {
        super(props);

        // State is used so that the value of the dropdown can be reset
        // each time a value is picked, otherwise the control doesn't always work
        this.state = {
            dropdownValue: -1,
        };

        this.handleCompleted = this.handleCompleted.bind(this);
        this.handleAddFixture = this.handleAddFixture.bind(this);
        this.isAddFixtureKeyPress = this.isAddFixtureKeyPress.bind(this);
    }

    handleAddFixture = async (
        addFixture,
        groupOnlyType,
        privacy,
        isIndividual,
        vesselImo
    ) => {
        const { currentGroup } = this.props;
        const defaultTypeFromLayout =
            this.props.selectedLayout.commonSettings.defaultType.currentState;

        const tankersGroupType = parseTankersGroupType(
            currentGroup?.groupSettings?.tankersGroupType
        );
        const availableTypes = typesByDataset(
            currentGroup?.datasetId,
            tankersGroupType
        );
        const isLayoutDefaultTypeAvailable = availableTypes.find(
            (x) => x === defaultTypeFromLayout
        );
        const type = isLayoutDefaultTypeAvailable
            ? defaultTypeFromLayout
            : getDefaultTypeForGroup(currentGroup?.datasetId, tankersGroupType);

        let vesselService;
        let vesselCategory;
        let owner;
        let vessel = await this.fetchVesselDetails(vesselImo);

        if (vesselImo) {
            vesselService = getVesselPostProcessingService(
                currentGroup?.datasetId
            );

            vesselCategory = getVesselCategory(vessel, currentGroup?.datasetId);
            owner = vesselService?.getNewOwnerObject(vessel);
        }
        const variables = {
            groupId: currentGroup?.id,
            groupOnly: groupOnlyType !== null,
            groupOnlyType,
            privacy,
            isIndividual,
            type,
            vesselImo: vesselImo,
            vesselCategory: vesselCategory,
            owner: owner,
        };
        addFixture({
            variables,
        });

        this.setState({
            dropdownValue: -1,
        });
    };

    async fetchVesselDetails(imo) {
        try {
            if (imo) {
                const { currentGroup } = this.props;
                return await VesselsApi.fetchVesselByImo(
                    currentGroup?.datasetId,
                    +imo
                );
            }
        } catch (error) {
            console.error('Error fetching vessel details:', error);
        }
    }
    isAddFixtureKeyPress = (event) => {
        if (event.altKey && event.ctrlKey && event.keyCode === KEY_F) {
            event.preventDefault();
            event.stopPropagation();
            return true;
        }
        return false;
    };

    handleCompleted(data) {
        const {
            id,
            privacy,
            groupName,
            isIndividual,
            datasetId,
            visibility,
            groupOnlyType,
        } = data.createFixture;
        const groupOnly = isGroupOnly(visibility);
        this.setState({ dropdownValue: -1 }, () => {
            if (this.props.onCompleted) {
                this.props.onCompleted(data.createFixture);
            }
        });

        MixpanelLogger.trackNewFixtureEvent(
            id,
            groupOnly,
            privacy,
            isIndividual,
            groupName,
            'Created',
            datasetId,
            groupOnlyType
        );
    }

    getOptions = () => {
        const currentGroup = store.getState().user.group;
        if (!currentGroup) return [];
        return getDocumentOptions('Fixture', currentGroup);
    };

    render = () => {
        return (
            <Mutation
                mutation={CREATE_FIXTURE_MUTATION}
                onCompleted={this.handleCompleted}
                fetchPolicy={'no-cache'}
            >
                {(addFixture, { loading }) => {
                    return (
                        <Fragment>
                            {!this.props.disabled && !loading && (
                                <GlobalShortcut
                                    onMatch={() =>
                                        this.handleAddFixture(
                                            addFixture,
                                            null,
                                            false,
                                            false,
                                            this.props.vesselImo
                                        )
                                    }
                                    keyMatcher={this.isAddFixtureKeyPress}
                                />
                            )}
                            <Dropdown
                                trigger={
                                    <ToolbarButton id="NewFixtureButton">
                                        <i className="icons plus-icon" />
                                        New Fixture
                                    </ToolbarButton>
                                }
                                options={this.getOptions()}
                                onChange={(e, data) => {
                                    /*
                                     * This ensures the user has actually clicked on a menu item as the dropdown onChange
                                     * gets triggered by a blur event. Defect 25602
                                     * Same logic used in AddOrder.jsx
                                     */
                                    if (isWithinMenu(e.target)) {
                                        const {
                                            groupOnly,
                                            groupOnlyType,
                                            isIndividual,
                                            privacy,
                                        } = resolveDocumentSelection(
                                            data.value,
                                            this.props.currentGroup?.datasetId
                                        );

                                        this.handleAddFixture(
                                            addFixture,
                                            groupOnly ? groupOnlyType : null,
                                            privacy,
                                            isIndividual,
                                            this.props.vesselImo
                                        );
                                    }
                                }}
                                value={this.state.dropdownValue}
                                disabled={this.props.disabled || loading}
                            />
                        </Fragment>
                    );
                }}
            </Mutation>
        );
    };
}

AddFixture.propTypes = {
    onCompleted: PropTypes.func,
    disabled: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
    return {
        currentGroup: state.user && state.user.group ? state.user.group : null,
        selectedLayout: state.layouts.selectedLayout,
    };
};

export default connect(mapStateToProps)(AddFixture);
